import { Box, Button, IconButton, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material"
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline"
import EditIcon from "@mui/icons-material/Edit"
import { collection, deleteDoc, doc, getDoc, getDocs, updateDoc } from "firebase/firestore"
import { useCallback, useEffect, useState } from "react"

import { db } from "../../firebase"
import { PopupService } from "react-ui-components/components/PopupService"

type FlattenedService = {
  id: string
  sponsorId: string
  sponsorName: string
  title?: string
  description?: string
  value?: number
  currency?: string
  disabled?: boolean
}

/**
 * Aggregates services across every sponsor's "services" subcollection into
 * a single cross-sponsor table. There is no top-level "services" Firestore
 * collection to back this with DataProvider/ListComponent, and creating one
 * server-side would cross the AGENTS.md backend boundary (functions repo),
 * so this fetches and flattens client-side instead.
 */
export const flattenSponsorServices = (
  sponsors: Array<{ id: string; name?: string }>,
  servicesBySponsor: Record<string, any[]>,
): FlattenedService[] =>
  sponsors.flatMap((sponsor) =>
    (servicesBySponsor[sponsor.id] || []).map((service) => ({
      id: service.id,
      sponsorId: sponsor.id,
      sponsorName: sponsor.name || sponsor.id,
      title: service.title,
      description: service.description,
      value: service.value,
      currency: service.currency,
      disabled: Boolean(service.disabled),
    })),
  )

export const StandaloneServicesComponent = () => {
  const [services, setServices] = useState<FlattenedService[]>([])
  const [loading, setLoading] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const sponsorsSnap = await getDocs(collection(db, "sponsors"))
      const sponsors = sponsorsSnap.docs.map((d) => ({ id: d.id, ...(d.data() as any) }))

      const servicesBySponsor: Record<string, any[]> = {}
      await Promise.all(
        sponsors.map(async (sponsor) => {
          const servicesSnap = await getDocs(collection(db, `sponsors/${sponsor.id}/services`))
          servicesBySponsor[sponsor.id] = servicesSnap.docs.map((d) => ({ id: d.id, ...d.data() }))
        }),
      )

      setServices(flattenSponsorServices(sponsors, servicesBySponsor))
    } catch (error: any) {
      PopupService.pushPopup(`Failed to load services: ${error.message}`)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const handleToggleDisabled = async (service: FlattenedService) => {
    setLoading(true)
    try {
      await updateDoc(doc(db, `sponsors/${service.sponsorId}/services`, service.id), {
        disabled: !service.disabled,
      })
      await load()
    } catch (error: any) {
      PopupService.pushPopup(`Failed to update service: ${error.message}`)
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async (service: FlattenedService) => {
    setLoading(true)
    try {
      await deleteDoc(doc(db, `sponsors/${service.sponsorId}/services`, service.id))
      await load()
    } catch (error: any) {
      PopupService.pushPopup(`Failed to delete service: ${error.message}`)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
        <Typography variant="h6">Services (all sponsors)</Typography>
        <Button onClick={() => load()} disabled={loading}>
          Refresh
        </Button>
      </Box>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Sponsor</TableCell>
            <TableCell>Title</TableCell>
            <TableCell>Price</TableCell>
            <TableCell>Disabled</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {services.map((service) => (
            <TableRow key={`${service.sponsorId}-${service.id}`}>
              <TableCell>{service.sponsorName}</TableCell>
              <TableCell>{service.title}</TableCell>
              <TableCell>{typeof service.value === "number" ? `${service.value} ${service.currency || ""}` : ""}</TableCell>
              <TableCell>{service.disabled ? "Yes" : "No"}</TableCell>
              <TableCell align="right">
                <IconButton size="small" onClick={() => handleToggleDisabled(service)} disabled={loading} aria-label="Toggle disabled">
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={() => handleDelete(service)} disabled={loading} aria-label="Delete service">
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  )
}

export default StandaloneServicesComponent
